import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { X, Clock, Flame, Heart, AlertTriangle, Zap } from "lucide-react";

export default function RenewalNudgeBanner() {
  const navigate = useNavigate();
  const [nudges, setNudges] = useState([]);
  const [currentNudge, setCurrentNudge] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    fetchNudges();
  }, []);

  const fetchNudges = async () => {
    try {
      const res = await axios.get(`${API}/renewal/nudges`);
      if (res.data.nudges?.length > 0) {
        setNudges(res.data.nudges);
        setCurrentNudge(res.data.nudges[0]);
      }
    } catch (error) {
      // Silently fail
    }
  };

  const dismissNudge = async () => {
    if (!currentNudge) return;
    
    try {
      await axios.post(`${API}/renewal/dismiss-nudge`, {
        nudge_type: currentNudge.type,
        nudge_value: currentNudge.days_remaining || currentNudge.milestone || 0
      });
    } catch (error) {
      // Continue anyway
    }
    
    // Move to next nudge or dismiss
    const nextNudges = nudges.slice(1);
    if (nextNudges.length > 0) {
      setNudges(nextNudges);
      setCurrentNudge(nextNudges[0]);
    } else {
      setDismissed(true);
    }
  };

  if (!currentNudge || dismissed) {
    return null;
  }

  // Determine styling based on urgency
  const getUrgencyStyle = () => {
    switch (currentNudge.urgency) {
      case "critical":
        return "bg-red-500/10 border-red-500/30 text-red-600";
      case "high":
        return "bg-amber-500/10 border-amber-500/30 text-amber-600";
      case "medium":
        return "bg-yellow-500/10 border-yellow-500/30 text-yellow-600";
      default:
        return "bg-blue-500/10 border-blue-500/30 text-blue-600";
    }
  };

  const getIcon = () => {
    switch (currentNudge.type) {
      case "trial_ending":
        return <Clock className="w-5 h-5" />;
      case "streak_milestone":
        return <Flame className="w-5 h-5" />;
      case "anniversary":
        return <Heart className="w-5 h-5" />;
      case "annual_renewal":
        return <Zap className="w-5 h-5" />;
      default:
        return <AlertTriangle className="w-5 h-5" />;
    }
  };

  const handleCTA = () => {
    if (currentNudge.type === "trial_ending" || currentNudge.type === "annual_renewal") {
      navigate("/pricing?source=renewal_nudge");
    } else {
      dismissNudge();
    }
  };

  return (
    <div className={`border-b ${getUrgencyStyle()}`}>
      <div className="max-w-7xl mx-auto px-4 py-3">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
              currentNudge.urgency === "critical" ? "bg-red-500/20" :
              currentNudge.urgency === "high" ? "bg-amber-500/20" :
              currentNudge.urgency === "medium" ? "bg-yellow-500/20" : "bg-blue-500/20"
            }`}>
              {getIcon()}
            </div>
            <div className="min-w-0">
              <div className="font-semibold text-sm truncate">{currentNudge.headline}</div>
              <div className="text-xs opacity-80 truncate">{currentNudge.message}</div>
            </div>
          </div>
          
          <div className="flex items-center gap-2 flex-shrink-0">
            {currentNudge.days_remaining !== undefined && (
              <span className={`hidden sm:inline-block text-xs font-bold px-2 py-1 rounded-full ${
                currentNudge.urgency === "critical" ? "bg-red-500/20" : "bg-current/10"
              }`}>
                {currentNudge.days_remaining} day{currentNudge.days_remaining !== 1 ? 's' : ''} left
              </span>
            )}
            {currentNudge.milestone && (
              <span className="hidden sm:inline-block text-xs font-bold bg-amber-500/20 px-2 py-1 rounded-full">
                {currentNudge.days_to_milestone} days to {currentNudge.milestone}-day streak!
              </span>
            )}
            <Button 
              size="sm" 
              onClick={handleCTA}
              variant={currentNudge.urgency === "critical" ? "destructive" : "default"}
            >
              {currentNudge.type.includes("trial") || currentNudge.type.includes("renewal") 
                ? "Manage on duolyst.co.uk" 
                : "Got It"}
            </Button>
            <button
              onClick={dismissNudge}
              className="p-1 hover:bg-muted rounded-full transition-colors"
            >
              <X className="w-4 h-4 opacity-60" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
